import { useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Analytics } from "@vercel/analytics/react";
import {
  isRateLimited,
  checkClientRateLimit,
  RateLimitError,
} from "../Services/aiReading";
import Deck from "../Components/Deck/Deck";
import Menu from "../Components/Menu/Menu";
import QuestionInput, {
  type ReadingType,
} from "../Components/QuestionInput/QuestionInput";
import ReadingDisplay from "../Components/ReadingDisplay/ReadingDisplay";
import ClarifyingReading from "../Components/ClarifyingReading/ClarifyingReading";
import SpreadReading from "../Components/SpreadReading/SpreadReading";
import AmbientBackground from "../Components/AmbientBackground/AmbientBackground";
import AboutPage from "../Components/AboutPage/AboutPage";
import FloatingSpeechButton from "../Components/FloatingSpeechButton/FloatingSpeechButton";
import UserButton from "../Components/UserButton/UserButton";
import FirstVisitHints from "../Components/FirstVisitHints/FirstVisitHints";
import { DarkModeProvider } from "../Services/Context/DarkModeContext";
import { SpeechProvider } from "../Services/Context/SpeechContext";
import { LanguageProvider } from "../Services/Context/LanguageContext";
import { AuthProvider } from "../Services/Context/AuthContext";
import { usePersistentPreferences } from "./usePersistentPreferences";
import { useReadingSession } from "./useReadingSession";
import { READING_PHASES } from "./types";
import "./App.css";

// I pray all four corners of this room welcome to any and all angels willing to take part in this message for the highest good of the seeker and with undeniably love for those here. May this prayer be said before any and all readins'.

function AppInner() {
  const {
    isReversedCardsEnabled,
    setIsReversedCardsEnabled,
    aiEnabled,
    setAiEnabled,
  } = usePersistentPreferences();

  const {
    phase,
    question,
    spreadType,
    isYesOrNo,
    drawnCard,
    deckKey,
    showClarifyingDeck,
    clarifyingCards,
    clarifyingDeckKey,
    clarifyingDeckRef,
    clarifyingActive,
    clarifyingLimitReached,
    excludedCardNames,
    handleQuestionSubmit,
    handleCardRevealed,
    handleDrawClarifying,
    handleClarifyingCardRevealed,
    handleDrawAgain,
  } = useReadingSession();

  const [showAbout, setShowAbout] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const menuClickArea = useRef<any>(null);

  const isReadingPhase = READING_PHASES.has(phase);

  const handleSubmit = (question: string, readingType: ReadingType) => {
    if (aiEnabled) {
      const isSingleCard =
        readingType === "single" || readingType === "yes-or-no";
      if (isSingleCard) {
        // Record the timestamp now so streamTarotReading can skip its check (avoids StrictMode double-count)
        try {
          checkClientRateLimit();
        } catch (err) {
          if (err instanceof RateLimitError) {
            setRateLimitError(err.message);
            return;
          }
        }
      } else {
        // Spread: just peek — SpreadReading records its own timestamp on first card flip
        const err = isRateLimited();
        if (err) {
          setRateLimitError(err);
          return;
        }
      }
    }
    setRateLimitError(null);
    handleQuestionSubmit(question, readingType);
  };

  const handleDrawAgainSafe = () => {
    setRateLimitError(null);
    handleDrawAgain();
  };

  const handleDrawClarifySafe = () => {
    if (aiEnabled) {
      try {
        checkClientRateLimit();
      } catch (err) {
        if (err instanceof RateLimitError) {
          setRateLimitError(err.message);
          return;
        }
      }
    }
    setRateLimitError(null);
    handleDrawClarifying();
  };

  const handleOutsideMenuClick = (eventTarget: EventTarget) => {
    if (menuClickArea.current && !menuClickArea.current.contains(eventTarget)) {
      setIsMenuClosing(true);
    }
  };

  return (
    <section
      className={`App${isReadingPhase ? " App--reading" : ""}`}
      onClick={(event) => handleOutsideMenuClick(event.target)}
    >
      <Analytics />
      <AmbientBackground />

      <AnimatePresence mode="wait">
        {phase === "question" && (
          <QuestionInput
            key="question-input"
            onSubmit={handleSubmit}
            onAboutClick={() => setShowAbout(true)}
            rateLimitError={rateLimitError}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAbout && <AboutPage onClose={() => setShowAbout(false)} />}
      </AnimatePresence>

      {(phase === "drawing" || phase === "reading") && (
        <Deck
          key={deckKey}
          isReversedCardsEnabled={isReversedCardsEnabled}
          onCardRevealed={handleCardRevealed}
        />
      )}

      {phase === "reading" && drawnCard && (
        <ReadingDisplay
          question={question}
          cardName={drawnCard.name}
          isReversed={drawnCard.isReversed}
          onDrawAgain={handleDrawAgainSafe}
          onDrawClarifying={
            clarifyingLimitReached ? undefined : handleDrawClarifySafe
          }
          clarifyingActive={clarifyingActive}
          aiEnabled={aiEnabled}
          isYesOrNo={isYesOrNo}
          rateLimitError={rateLimitError}
        />
      )}

      {phase === "reading" &&
        clarifyingCards.map((card, cardIndex) => {
          const isLastCard = cardIndex === clarifyingCards.length - 1;
          const showButtons = isLastCard && !showClarifyingDeck;
          return (
            <ClarifyingReading
              key={cardIndex}
              question={question}
              cardName={card.name}
              isReversed={card.isReversed}
              image={card.image}
              originalCard={drawnCard!}
              previousClarifyingCards={clarifyingCards
                .slice(0, cardIndex)
                .map((previousCard) => ({
                  name: previousCard.name,
                  isReversed: previousCard.isReversed,
                }))}
              onDrawClarifying={
                showButtons && !clarifyingLimitReached
                  ? handleDrawClarifySafe
                  : undefined
              }
              onDrawAgain={showButtons ? handleDrawAgainSafe : undefined}
              rateLimitError={showButtons ? rateLimitError : undefined}
              aiEnabled={aiEnabled}
            />
          );
        })}

      {phase === "reading" && showClarifyingDeck && (
        <div ref={clarifyingDeckRef}>
          <Deck
            key={clarifyingDeckKey}
            isReversedCardsEnabled={isReversedCardsEnabled}
            onCardRevealed={handleClarifyingCardRevealed}
            excludedCardNames={excludedCardNames}
          />
        </div>
      )}

      {phase === "spread" && spreadType && (
        <SpreadReading
          spreadType={spreadType}
          question={question}
          isReversedCardsEnabled={isReversedCardsEnabled}
          onDrawAgain={handleDrawAgainSafe}
          aiEnabled={aiEnabled}
        />
      )}

      <FloatingSpeechButton active={isReadingPhase} />
      <UserButton />
      <FirstVisitHints phase={phase} />

      {phase === "question" && (
        <Menu
          setIsReversedCardsEnabled={setIsReversedCardsEnabled}
          isReversedCardsEnabled={isReversedCardsEnabled}
          aiEnabled={aiEnabled}
          setAiEnabled={setAiEnabled}
          menuClickArea={menuClickArea}
          isMenuClosing={isMenuClosing}
          setIsMenuClosing={setIsMenuClosing}
        />
      )}
    </section>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <DarkModeProvider>
          <SpeechProvider>
            <AppInner />
          </SpeechProvider>
        </DarkModeProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
